import React, { useEffect, useMemo, useRef, useState } from "react";
import Slider from "react-slick";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { useIsInViewport, useWindowSize } from "../../../hooks";
import {
  baseLogoCarouselSettings,
  getLogoCarouselSlidesToShow,
} from "../logoCarouselSettings";
import "./SchoolsCarousel.scss";
import { Image } from "../../common/image/Image";

// Logos (hardcoded imports)
import YeshivaUniversity from "../../../assets/images/schools/YeshivaUniversity.svg";
import HarvardUniversity from "../../../assets/images/schools/HarvardUniversity.svg";
import PaceUniversity from "../../../assets/images/schools/PaceUniversity.svg";
import UniversityAtAlbany from "../../../assets/images/schools/UniversityAtAlbany.svg";
import IthacaCollege from "../../../assets/images/schools/IthacaCollege.svg";
import UniversityOfRochester from "../../../assets/images/schools/UniversityOfRochester.svg";
import FordhamUniversity from "../../../assets/images/schools/FordhamUniversity.svg";
import BaruchCollege from "../../../assets/images/schools/BaruchCollege.svg";
import NewYorkUniversity from "../../../assets/images/schools/NewYorkUniversity.svg";
import SyracuseUniversity from "../../../assets/images/schools/SyracuseUniversity.svg";
import ColumbiaUniversity from "../../../assets/images/schools/ColumbiaUniversity.png";
import Scranton from "../../../assets/images/schools/Scranton.png";

const logos = [
  { img: YeshivaUniversity },
  { img: HarvardUniversity },
  { img: PaceUniversity },
  { img: UniversityAtAlbany },
  { img: IthacaCollege },
  { img: UniversityOfRochester },
  { img: FordhamUniversity },
  { img: BaruchCollege },
  { img: NewYorkUniversity },
  { img: SyracuseUniversity },
  { img: ColumbiaUniversity },
  { img: Scranton },
];

const ArrowStyles = {
  color: "#F9DF1C",
  fontSize: "5rem",
};

const NextArrow = ({ className, onClick }) => (
  <ArrowForwardIosIcon
    className={className}
    sx={{ ...ArrowStyles, transform: "translate(3rem, -50%)" }}
    onClick={onClick}
  />
);

const PrevArrow = ({ className, onClick }) => (
  <ArrowBackIosNewIcon
    className={className}
    sx={{ ...ArrowStyles, transform: "translate(-3rem, -50%)" }}
    onClick={onClick}
  />
);

const SchoolsCarousel = ({ title = "Schools Our Students Attend" }) => {
  const windowSize = useWindowSize();
  const isInViewportThreshold = windowSize.width > 768 ? 0.5 : 0.2;
  const sectionRef = useRef(null);
  const sliderRef = useRef(null);
  const isInViewport = useIsInViewport(sectionRef, isInViewportThreshold);
  const [showAnimation, setShowAnimation] = useState(false);
  const slickSettings = useMemo(
    () => ({
      ...baseLogoCarouselSettings,
      slidesToShow: getLogoCarouselSlidesToShow(windowSize.width),
      nextArrow: <NextArrow />,
      prevArrow: <PrevArrow />,
    }),
    [windowSize.width]
  );

  useEffect(() => {
    if (isInViewport) {
      setShowAnimation(true);
    }
  }, [isInViewport]);

  useEffect(() => {
    if (showAnimation && sliderRef.current?.innerSlider) {
      sliderRef.current.innerSlider.onWindowResized();
    }
  }, [showAnimation, windowSize.width, slickSettings.slidesToShow]);

  return (
    <section
      className={`section section--schoolsCarousel${
        showAnimation ? " onScreen" : ""
      }`}
      ref={sectionRef}
    >
      <div className="section__innerWrapper">
        <article className="overDeco">
          <h3 className="center animateIn">{title}</h3>
        </article>
        <Slider ref={sliderRef} {...slickSettings}>
          {logos.map((school, index) => (
            <div
              className="slide-wrapper"
              key={index}
            >
              <Image
                src={school.img}
                alt={`School logo ${index + 1}`}
              />
            </div>
          ))}
        </Slider>
        <p className="schoolsDisclaimer">
          The educational institutions above are not affiliated with YELO
          Funding, nor do they endorse YELO’s Income Contingent Financing in any
          way.
        </p>
      </div>
    </section>
  );
};

export default SchoolsCarousel;
